import { useState, useMemo } from 'react';
import {
  Combobox,
  ComboboxTrigger,
  ComboboxContent,
  ComboboxInput,
  ComboboxList,
  ComboboxItem,
  ComboboxEmpty,
  ComboboxGroup,
  StepNumberBadge
} from '@bliks-io/ui';
import { STEP_ACTION_ICONS } from '@/components/processes/step-action-icons';

export interface StepSelectOption {
  /** Unique id (stepId or composite key for decision rules) */
  value: string;
  /** generatedName / step name */
  label: string;
  description?: string;
  /** "Recorded by X" or "Output: Y" */
  metadata?: string;
  actionType?: 'task' | 'decision';
  /** Flow step number, shown as a badge mirroring the canvas. */
  stepNumber?: number;
}

interface StepRecordingSelectProps {
  options: StepSelectOption[];
  value: string | null;
  onValueChange: (value: string | null) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  disabled?: boolean;
  /** Label text above the trigger */
  label?: string;
  className?: string;
}

export function StepRecordingSelect({
  options,
  value,
  onValueChange,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  disabled = false,
  label,
  className
}: StepRecordingSelectProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    const q = searchQuery.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(q) ||
        opt.description?.toLowerCase().includes(q) ||
        opt.metadata?.toLowerCase().includes(q)
    );
  }, [options, searchQuery]);

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value) ?? null,
    [options, value]
  );

  const handleValueChange = (v: string | string[] | undefined) => {
    onValueChange(typeof v === 'string' ? v : null);
  };

  const handleClear = () => {
    onValueChange(null);
  };

  return (
    <div className={className}>
      {label && <label className="mb-1.5 block text-sm font-medium">{label}</label>}
      <Combobox
        value={value ?? undefined}
        onValueChange={handleValueChange}
        onClear={handleClear}
        disabled={disabled}
      >
        <ComboboxTrigger placeholder={placeholder} allowClear={!!value}>
          {selectedOption && (
            <span className="flex min-w-0 items-center gap-1.5">
              {selectedOption.stepNumber != null && (
                <StepNumberBadge number={selectedOption.stepNumber} />
              )}
              <span className="truncate">{selectedOption.label}</span>
            </span>
          )}
        </ComboboxTrigger>
        <ComboboxContent>
          <ComboboxInput
            placeholder={searchPlaceholder}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <ComboboxList>
            <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
            <ComboboxGroup>
              {filteredOptions.map((option) => {
                const Icon = option.actionType ? STEP_ACTION_ICONS[option.actionType] : null;
                return (
                  <ComboboxItem key={option.value} value={option.value}>
                    <div className="flex items-start gap-2.5">
                      {Icon && <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
                      <div className="flex min-w-0 flex-col">
                        <span className="flex items-center gap-1.5 font-medium">
                          {option.stepNumber != null && (
                            <StepNumberBadge number={option.stepNumber} />
                          )}
                          <span className="truncate">{option.label}</span>
                        </span>
                        {option.description && (
                          <span className="line-clamp-2 text-xs text-muted-foreground/70">
                            {option.description}
                          </span>
                        )}
                        {option.metadata && (
                          <span className="text-xs text-muted-foreground">{option.metadata}</span>
                        )}
                      </div>
                    </div>
                  </ComboboxItem>
                );
              })}
            </ComboboxGroup>
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  );
}
